import { z } from 'zod'

// Individual log entry schema
export const cloudWatchLogEntrySchema = z.object({
  timestamp: z.string(),
  message: z.string(),
  status_code: z.string(),
})

export type CloudWatchLogEntry = z.infer<typeof cloudWatchLogEntrySchema>

// Pagination schema for cursor-based server-side pagination
export const cloudWatchPaginationSchema = z.object({
  has_more: z.boolean(),
  next_cursor: z.string().nullable().optional(), // May be absent when has_more is false
})

export type CloudWatchPagination = z.infer<typeof cloudWatchPaginationSchema>

// Lambda log group schema
export const cloudWatchLambdaLogSchema = z.object({
  stack_name: z.string(),
  lambda_function_name: z.string(),
  log_group: z.string(),
  region: z.string(),
  logs_found: z.number(),
  count_4xx: z.number(),
  count_5xx: z.number(),
  logs: z.array(cloudWatchLogEntrySchema),
  pagination: cloudWatchPaginationSchema.nullable().optional(),
})

export type CloudWatchLambdaLog = z.infer<typeof cloudWatchLambdaLogSchema>

// API response schema
export const cloudWatchLogsApiResponseSchema = z.object({
  workstream: z.string(),
  environment: z.string().nullable(),
  total_logs: z.number(),
  count_4xx: z.number(),
  count_5xx: z.number(),
  lambda_logs: z.array(cloudWatchLambdaLogSchema),
  pagination: cloudWatchPaginationSchema.nullable().optional(),
})

export type CloudWatchLogsApiResponse = z.infer<typeof cloudWatchLogsApiResponseSchema>

// Flattened log entry for table display
export const cloudWatchTableRowSchema = z.object({
  id: z.string(), // Unique ID for row (combination of lambda + timestamp)
  error_message: z.string(),
  error_code: z.string(),
  lambda_function: z.string(),
  log_group: z.string(),
  stack_name: z.string(),
  region: z.string(),
  timestamp: z.string(),
  full_message: z.string(), // Full JSON message for detail panel
})

export type CloudWatchTableRow = z.infer<typeof cloudWatchTableRowSchema>

// CloudWatch counts API response schema
export const cloudWatchCountsApiResponseSchema = z.object({
  workstream: z.string(),
  environment: z.string().nullable(),
  total_logs: z.number(),
  count_4xx: z.number(),
  count_5xx: z.number(),
  total_errors: z.number(),
})

export type CloudWatchCountsApiResponse = z.infer<typeof cloudWatchCountsApiResponseSchema>
